import {
	CountrySchema,
	DateOfBirthSchema,
	noop,
	useCountries,
	useIntl,
	type UserRequirementStateStatus
} from '@capsicum/common';
import { useMe } from '@capsicum/socios-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { customRequirementsAtom, useUpdateRequirementToWaitingByIdentifierQuery } from '../data-access';

export type SumsubIdentityVerificationFormType = z.infer<ReturnType<typeof useSumsubIdentityVerificationFormSchema>>;

const useSumsubIdentityVerificationFormSchema = () => {
	const { $t } = useIntl();

	return z.object({
		addressCountry: CountrySchema,
		dateOfBirth: DateOfBirthSchema({
			$t,
			checkAgeOfMajority: true
		}),
		firstName: z.string(),
		lastName: z.string(),
		nationality: CountrySchema
	});
};

export const useSumsubRequirement = (status?: UserRequirementStateStatus) => {
	const { isMeLoading, me } = useMe();
	const { mutateAsync: updateIdentityVerificationRequirementToWaiting } =
		useUpdateRequirementToWaitingByIdentifierQuery();
	const { countriesQuery, getCountryBy } = useCountries();
	const { requirements } = useAtomValue(customRequirementsAtom);

	const defaultValues = {
		addressCountry: getCountryBy(me.address?.countryCode, 'iso2'),
		dateOfBirth: me.birthDate,
		firstName: me.firstName || '',
		lastName: me.lastName || '',
		nationality: getCountryBy(me.nationalityCountryCode, 'iso2')
	};

	const sumsubCompletedForm = useForm<SumsubIdentityVerificationFormType>({
		defaultValues,
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(useSumsubIdentityVerificationFormSchema())
	});

	useEffect(() => {
		if (countriesQuery.isFetched) sumsubCompletedForm.reset(defaultValues);
	}, [countriesQuery.isFetched, countriesQuery.isFetching]);

	const submitIdentityVerification = async ({ onSuccess = noop } = {}) => {
		await updateIdentityVerificationRequirementToWaiting(
			{
				requirementIdentifier: 'proof_of_id'
			},
			{ onSuccess }
		);
	};

	const hideSumsubSdk = ['waiting', 'under_review', 'completed_deny'].includes(status || '');
	const isRequirementFulfilled = status === 'completed_allow';
	const isCustomRequirement = requirements?.includes('proof_of_id');

	return {
		hideSumsubSdk,
		isCustomRequirement,
		isRequirementFulfilled,
		isRequirementLoading: isMeLoading,
		submitIdentityVerification,
		sumsubCompletedForm
	};
};
